import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import formatDate from "./formatDate";

export const generateInvoice = async (type = "pdf", data) => {
  try {
    // Validate inputs
    if (!data) {
      throw new Error("Invoice data is required");
    }

    if (!["pdf", "image"].includes(type)) {
      throw new Error('Type must be either "pdf" or "image"');
    }

    // Create invoice HTML element
    const element = await createInvoiceElement(data);

    // Create canvas with optimized settings
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      onclone: (clonedDoc) => {
        // Ensure fonts and styles are loaded in the cloned document
        const clonedElement = clonedDoc.getElementById("invoice-template");
        if (clonedElement) {
          clonedElement.style.visibility = "visible";
        }
      },
    });

    const imgData = canvas.toDataURL("image/png", 1.0);

    // -------------------------
    // RETURN IMAGE
    // -------------------------
    if (type === "image") {
      // Convert base64 → Blob
      const response = await fetch(imgData);
      const blob = await response.blob();

      // Create downloadable file
      const fileName = `invoice-${
        data.invoice.invoiceNumber || data.id || "unknown"
      }.png`;
      return {
        blob,
        fileName,
        url: URL.createObjectURL(blob),
        type: "image/png",
      };
    }

    // -------------------------
    // RETURN PDF (fit image to A4, preserve aspect, set orientation)
    // -------------------------
    // choose orientation based on image aspect ratio
    const dpi = 96; // canvas DPI assumption
    const imgWpx = canvas.width;
    const imgHpx = canvas.height;
    const orientation = imgWpx > imgHpx ? "landscape" : "portrait";

    const pdf = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // convert image px → mm (1in = 25.4mm, assume 96 dpi)
    const imgWidthMm = (imgWpx * 25.4) / dpi;
    const imgHeightMm = (imgHpx * 25.4) / dpi;

    const margin = 10; // mm
    const maxWidth = pdfWidth - margin * 2;
    const maxHeight = pdfHeight - margin * 2;

    const scale = Math.min(maxWidth / imgWidthMm, maxHeight / imgHeightMm, 1);
    const finalWidth = imgWidthMm * scale;
    const finalHeight = imgHeightMm * scale;

    const x = (pdfWidth - finalWidth) / 2;
    const y = (pdfHeight - finalHeight) / 2;

    // add image centered, preserve quality / speed
    pdf.addImage(imgData, "PNG", x, y, finalWidth, finalHeight, undefined, "FAST");

    // Add metadata
    const invoiceNumber = data.invoiceNumber || data.id || "unknown";
    pdf.setProperties({
      title: `Invoice ${invoiceNumber}`,
      subject: `Medical Invoice for ${data.patient?.firstName || ""} ${
        data.patient?.lastName || ""
      }`,
      creator: "Raqib HealthCare",
      author: "Raqib HealthCare System",
    });

    const fileName = `invoice-${invoiceNumber}.pdf`;

    return {
      pdf,
      fileName,
      blob: pdf.output("blob"),
      url: pdf.output("bloburl"),
      type: "application/pdf",
    };
  } catch (error) {
    // Handle error silently
    throw new Error(`Failed to generate invoice: ${error.message}`);
  }
};

// Helper function to create invoice HTML element
const createInvoiceElement = async (data) => {
  const invoiceContainer = document.createElement("div");
  invoiceContainer.id = "invoice-template";
  invoiceContainer.style.cssText = `
    width: 210mm;
    min-height: 297mm;
    padding: 20mm;
    background: white;
    color: black;
    font-family: 'Arial', sans-serif;
    box-sizing: border-box;
    position: absolute;
    left: -9999px;
    top: -9999px;
  `;

  // Invoice HTML template
  invoiceContainer.innerHTML = `
    <div style="border: 2px solid #333; padding: 20px; border-radius: 8px; position: relative; overflow: hidden;">
      
      <!-- Watermark -->
   
  ${
    data.clinicLogo
      ? `
    <div style="position: absolute; top: 10px; left: 10px; transform: rotate(-30deg); opacity: 0.05; z-index: 0;">
      <img src="${data.clinicLogo}" alt="Watermark Logo" style="width: 200px; height: auto; object-fit: contain;" />
    </div>
    <div style="position: absolute; top: 10px; right: 10px; transform: rotate(10deg); opacity: 0.05; z-index: 0;">
      <img src="${data.clinicLogo}" alt="Watermark Logo" style="width: 200px; height: auto; object-fit: contain;" />
    </div>
    <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-5deg); opacity: 0.05; z-index: 0;">
      <img src="${data.clinicLogo}" alt="Watermark Logo" style="width: 200px; height: auto; object-fit: contain;" />
    </div>
    <div style="position: absolute; bottom: 10px; left: 10px; transform: rotate(20deg); opacity: 0.05; z-index: 0;">
      <img src="${data.clinicLogo}" alt="Watermark Logo" style="width: 200px; height: auto; object-fit: contain;" />
    </div>
    <div style="position: absolute; bottom: 10px; right: 10px; transform: rotate(-10deg); opacity: 0.05; z-index: 0;">
      <img src="${data.clinicLogo}" alt="Watermark Logo" style="width: 200px; height: auto; object-fit: contain;" />
    </div>
  `
      : ""
  }

      <!-- Content Wrapper (above watermark) -->
      <div style="position: relative; z-index: 1;">
        
        <!-- Header with Logo -->
        <div style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px;">
          ${
            data.clinicLogo
              ? `
            <div style="margin-bottom: 15px; background: transparent;">
              <img src="${data.clinicLogo}" alt="Clinic Logo" style="max-width: 120px; max-height: 80px; object-fit: contain; background: transparent;" />
            </div>
          `
              : ""
          }
          <h1 style="color: #2c5aa0; margin: 0; font-size: 28px;">${
            data.user.clinic?.name || "RAQIB HEALTHCARE"
          }</h1>
          <p style="color: #666; margin: 5px 0; font-size: 14px;">Medical Invoice</p>
          <p style="color: #666; margin: 0; font-size: 12px;">${new Date().toLocaleDateString()}</p>
        </div>
      
        <!-- Verification Badge -->
        <div style="text-align: center; margin-bottom: 20px;">
  <span style="display: inline-block; padding: 6px 12px;  color: #2c5aa0; border-radius: 12px; font-size: 12px; font-weight: bold;">
    &#10004; Verified Invoice
  </span>
  <br />
  <span style="display: inline-block; font-size: 10px; color: #666; margin-top: 4px;">
    Badeg code : ${data.invoice.barCode}
  </span>
</div>


        <!-- Invoice Details with QR Code -->
        <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 30px; align-items: start;">
          <div>
            <h3 style="color: #333; margin-bottom: 10px; font-size: 16px;">Invoice Information</h3>
            <table style="width: 100%; font-size: 12px;">
              <tr>
                <td style="padding: 4px 0; color: #666;"><strong>Invoice Number:</strong></td>
                <td style="padding: 4px 0;">${
                  data.invoice.invoiceNumber || "N/A"
                }</td>
              </tr>
              <tr>
                <td style="padding: 4px 0; color: #666;"><strong>Invoice Date:</strong></td>
                <td style="padding: 4px 0;">${
                  data.invoice.invoiceDate
                    ? new Date(data.invoice.invoiceDate).toLocaleDateString()
                    : "N/A"
                }</td>
              </tr>
              <tr>
                <td style="padding: 4px 0; color: #666;"><strong>Payment Method:</strong></td>
                <td style="padding: 4px 0; text-transform: capitalize;">${
                  data?.method || data.paymentMethod || "N/A"
                }</td>
              </tr>
            </table>
          </div>
          
          <!-- QR Code -->
          ${
            data.qrCodeImage
              ? `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
              <div style="text-align: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                <img src="${data.qrCodeImage}" alt="QR Code" style="width: 90px; height: 90px;" />
              </div>
              <p style="font-size: 10px; color: #666; margin-top: 5px; text-align: center;">Scan for Payment Details</p>
            </div>
          `
              : ""
          }
        </div>

        <!-- From and To -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
          <div>
            <h3 style="color: #333; margin-bottom: 15px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">Billed By</h3>
            <p style="font-size: 12px; margin: 4px 0;"><strong>${
              data.user.clinic?.name || "N/A"
            }</strong></p>
            <p style="font-size: 12px; margin: 4px 0;">${
              data.user.clinic?.address || "N/A"
            }</p>
            <p style="font-size: 12px; margin: 4px 0;">Email: ${
              data.user.clinic?.email || data.user?.email || "N/A"
            }</p>
            <p style="font-size: 12px; margin: 4px 0;">Phone: ${
              data.user.clinic?.phone || "N/A"
            }</p>
          </div>
          <div>
            <h3 style="color: #333; margin-bottom: 15px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">Billed To</h3>
            <p style="font-size: 12px; margin: 4px 0;"><strong>${
              data.patient?.firstName || "N/A"
            } ${data.patient?.lastName || "N/A"}</strong></p>
            <p style="font-size: 12px; margin: 4px 0;">${
              data.patient?.address || "N/A"
            }</p>
            <p style="font-size: 12px; margin: 4px 0;">Email: ${
              data.patient?.email || "N/A"
            }</p>
            <p style="font-size: 12px; margin: 4px 0;">Phone: ${
              data.patient?.phone || "N/A"
            }</p>
          </div>
        </div>

        <!-- Amount Breakdown -->
        <div style="margin-bottom: 30px;">
          <h3 style="color: #333; margin-bottom: 15px; font-size: 16px; border-bottom: 1px solid #ddd; padding-bottom: 8px;">Amount Breakdown</h3>
          <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #ddd; color: #666;"><strong>Description</strong></td>
              <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right; color: #666;"><strong>Amount</strong></td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #ddd;">Original Amount</td>
              <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">$${Number(
                data.payment?.amount || data.amount || 0
              ).toFixed(2)}</td>
            </tr>
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #ddd;">Discount (${
                data.payment?.discountPercent || data.discountPercent || 0
              }%)</td>
              <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">-$${Number(
                data.payment?.discountAmount || data.discountAmount || 0
              ).toFixed(2)}</td>
            </tr>
            <tr style="background-color: #f8f9fa;">
              <td style="padding: 12px 8px; border-bottom: 2px solid #333;"><strong>Final Amount</strong></td>
              <td style="padding: 12px 8px; border-bottom: 2px solid #333; text-align: right; font-weight: bold; color: #2c5aa0; font-size: 14px;">
                $${Number(
                  data.payment?.amountAfterDiscount ||
                    data.finalAmount ||
                    data.amount ||
                    0
                ).toFixed(2)}
              </td>
            </tr>
          </table>
        </div>

        <!-- Barcode Section -->
        ${
          data.barCode || data.codeBarImage
            ? `
          <div style="text-align: center; margin-top: 40px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
            <p style="font-size: 10px; color: #666; margin-bottom: 10px;">Invoice Barcode</p>
            ${
              data.codeBarImage
                ? `<img src="${data.codeBarImage}" alt="Barcode" style="max-width: 250px; height: auto;" />`
                : `<p style="font-family: monospace; font-size: 14px; letter-spacing: 2px;">${data.barCode}</p>`
            }
          </div>
        `
            : ""
        }

        <!-- Footer -->
        <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd;">
          <p style="font-size: 10px; color: #666; line-height: 1.6;">
            Thank you for choosing ${
              data.user.clinic?.name || "Raqib Healthcare"
            }<br />
            This is a computer-generated invoice and does not require a signature
          </p>
        </div>
      </div>
    </div>
  `;

  // --- Inline remote images using cookies only ---
  const blobToDataUrl = (blob) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

  const fetchImageAsDataUrl = async (src) => {
    if (!src) return src;
    try {
      let url = src;
      // Resolve leading slash file paths to absolute url
      if (url.startsWith("/")) url = `${window.location.origin}${url}`;

      // If already a data URL, just return it
      if (url.startsWith("data:")) return url;

      // Fetch with credentials (cookies) only - no authorization header
      const res = await fetch(url, {
        credentials: "include", // Include cookies for authentication
      });

      if (!res.ok) {
        // Handle error silently
        return src; // Fallback to original
      }

      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      return dataUrl;
    } catch (err) {
      // Handle error silently
      return src; // Fallback
    }
  };

  // Find images in the generated markup and inline them
  const imgs = Array.from(invoiceContainer.getElementsByTagName("img"));
  await Promise.all(
    imgs.map(async (img) => {
      const original = img.getAttribute("src");
      if (!original) return;
      const dataUrl = await fetchImageAsDataUrl(original);
      // Set crossOrigin just in case (data URLs ignore it)
      img.crossOrigin = "anonymous";
      img.src = dataUrl;
    })
  );
  // --- END inlining ---

  // Append to body temporarily
  document.body.appendChild(invoiceContainer);

  // Wait for images to load
  await waitForImages(invoiceContainer);

  return invoiceContainer;
};
// Helper function to wait for images to load (tolerant to errors)
const waitForImages = (element) => {
  const images = element.getElementsByTagName("img");
  const imagePromises = Array.from(images).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => {
        // Handle error silently
        resolve();
      };
    });
  });
  return Promise.all(imagePromises);
};

// Utility function to download the generated file
export const downloadInvoice = (invoiceData, filename) => {
  const url = invoiceData.url || URL.createObjectURL(invoiceData.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || invoiceData.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL if we created it
  if (!invoiceData.url) {
    URL.revokeObjectURL(url);
  }
};

// Usage example:
/*
try {
  const invoiceData = {
    invoiceNumber: "INV-1764250948544",
    invoiceDate: "2025-11-27T13:42:28.568Z",
    barCode: "12-a0d125b75a-198db01e",
    codeBarImage: "/payments/uploads/barCodes/invoices/INV-1764250948544.png",
    uniqueId: "ed193df1-262f-4b78-aee3-074152384d1d",
    payment: {
      amount: 150.00,
      discountPercent: 10,
      amountAfterDiscount: 135.00,
      method: "credit-card",
      paymentStatus: "paid"
    },
    patient: {
      firstName: "John",
      lastName: "Doe",
      gender: "Male",
      age: 35,
      phone: "+1234567890"
    }
  };

  // Generate PDF
  const pdfInvoice = await generateInvoice("pdf", invoiceData);
  downloadInvoice(pdfInvoice);

  // Generate Image
  const imageInvoice = await generateInvoice("image", invoiceData);
  downloadInvoice(imageInvoice);

} catch (error) {
  console.error("Error:", error);
}
*/
